/**
 * Template Selection Step
 *
 * First step of company setup wizard - choose business template
 */

import { useState } from 'react'

import { TEMPLATE_METADATA } from '../../constants/coa-templates'

import type { BusinessTemplate } from '../../types/company-setup'

interface TemplateSelectionStepProps {
  selectedTemplate: BusinessTemplate
  onSelect: (template: BusinessTemplate) => void
  onNext: () => void
}

const TEMPLATE_ICONS: Record<BusinessTemplate, JSX.Element> = {
  'small_business': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
    </svg>
  ),
  'service_based': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
    </svg>
  ),
  retail: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
    </svg>
  ),
  manufacturing: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
    </svg>
  ),
  'professional_services': (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
    </svg>
  ),
  nonprofit: (
    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} />
    </svg>
  ),
}

interface TemplateCardProps {
  template: (typeof TEMPLATE_METADATA)[BusinessTemplate]
  isSelected: boolean
  isHovered: boolean
  onSelect: (id: BusinessTemplate) => void
  onHover: (id: BusinessTemplate | null) => void
}

function TemplateCard({ template, isSelected, isHovered, onSelect, onHover }: TemplateCardProps): JSX.Element {
  return (
    <button
      className={`relative p-6 rounded-lg border-2 transition-all text-left ${isSelected ? 'border-primary bg-muted' : 'border-border hover:border-primary'}`}
      type="button"
      onClick={(): void => onSelect(template.id)}
      onMouseEnter={(): void => onHover(template.id)}
      onMouseLeave={(): void => onHover(null)}
    >
      {isSelected && (
        <div className="absolute top-4 right-4">
          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path clipRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" fillRule="evenodd" />
            </svg>
          </div>
        </div>
      )}
      <div className={`mb-4 ${isSelected ? 'text-primary' : 'text-foreground-secondary'}`}>{TEMPLATE_ICONS[template.id]}</div>
      <h3 className="text-lg font-semibold text-foreground mb-2">{template.name}</h3>
      <p className="text-sm text-foreground-secondary mb-3">{template.description}</p>
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
        <span>{template.accountCount} accounts included</span>
      </div>
      <div className={`mt-3 flex flex-wrap gap-1 transition-opacity duration-200 ${isHovered ? 'opacity-100' : 'opacity-0 invisible'}`}>
        {template.industries.map((industry) => (
          <span key={industry} className="text-xs px-2 py-1 rounded-full bg-surface-secondary text-foreground-secondary">{industry}</span>
        ))}
      </div>
    </button>
  )
}

function SelectedTemplateInfo({ selectedTemplate }: { selectedTemplate: BusinessTemplate }): JSX.Element {
  const meta = TEMPLATE_METADATA[selectedTemplate]
  return (
    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
      <div className="flex items-start gap-3">
        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path clipRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" fillRule="evenodd" />
        </svg>
        <div>
          <p className="text-sm font-medium text-blue-900 dark:text-blue-100">Selected: {meta.name}</p>
          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
            This template includes a curated set of {meta.accountCount} accounts
            commonly used in {meta.industries[0]?.toLowerCase() ?? 'various'} businesses.
            You&apos;ll be able to customize these in the next steps.
          </p>
        </div>
      </div>
    </div>
  )
}

export function TemplateSelectionStep({ selectedTemplate, onSelect, onNext }: TemplateSelectionStepProps): JSX.Element {
  const [hoveredTemplate, setHoveredTemplate] = useState<BusinessTemplate | null>(null)
  const templates = Object.values(TEMPLATE_METADATA)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            isHovered={hoveredTemplate === template.id}
            isSelected={selectedTemplate === template.id}
            template={template}
            onHover={setHoveredTemplate}
            onSelect={onSelect}
          />
        ))}
      </div>
      {selectedTemplate && <SelectedTemplateInfo selectedTemplate={selectedTemplate} />}
      <div className="flex justify-end pt-4">
        <button
          className="px-3 py-2 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          disabled={!selectedTemplate}
          type="button"
          onClick={onNext}
        >
          Next: Company Information
        </button>
      </div>
    </div>
  )
}
